import React, { useState, useRef, useMemo, useCallback } from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    TextField, 
    Button, 
    Box,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    ListSubheader,
    Typography,
    Tooltip
} from '@mui/material';
import { useLanguage } from '../../../../../context/LanguageContext';
import { Store } from '../../../../../types/Store';
import useUpdateStore from '../../../../../hooks/Magasins/useUpdateStore';
import { useOptimisticStore } from '../../../../../context/OptimisticStoreContext';
import { toast } from 'react-toastify';
import { getStoreSearchLabel } from '../../../../../utils/storeHelpers';
import { StoreMultiAutocomplete } from '../../../../../components/Common';

interface EditAdditionalInfoDrawerProps {
    open: boolean;
    onClose: () => void;
    store: Store;
    onSuccess?: () => void;
    listStoresOptions?: any[];
    isCompany?: boolean;
}

const EditAdditionalInfoDrawer = ({ 
    open, 
    onClose, 
    store, 
    onSuccess,
    listStoresOptions = [],
    isCompany = false
}: EditAdditionalInfoDrawerProps) => {
    const { t } = useLanguage();
    const { updateStore, isLoading } = useUpdateStore();
    const { actualStore, setActualStore } = useOptimisticStore();

    const latitudeRef = useRef<HTMLInputElement | null>(null);
    const longitudeRef = useRef<HTMLInputElement | null>(null);
    const subscribeDateRef = useRef<HTMLInputElement | null>(null);
    const unsubscribeDateRef = useRef<HTMLInputElement | null>(null);
    const signatureRef = useRef<HTMLInputElement | null>(null);
    const descriptionRef = useRef<HTMLInputElement | null>(null);
    const openingHoursRef = useRef<HTMLInputElement | null>(null);

    const formatDateForInput = (dateString?: string | null): string => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const [formData, setFormData] = useState({
        depends: store.PARENT || [],
        group: store.CHILDREN || [],
    });

    // Fonction pour normaliser le type d'entité
    const normalizeEntityType = useCallback((type?: string) => {
        if (!type) return null;
        return type;
    }, []);
    
    // Règles de dépendance: détermine si un magasin peut dépendre d'un autre
    const canDependOn = useCallback((storeType: string | null | undefined, dependOnType: string | null | undefined) => {
        const normalizedStoreType = normalizeEntityType(storeType || undefined);
        const normalizedDependType = normalizeEntityType(dependOnType || undefined);
        
        // Un magasin STATISTIC ne peut pas dépendre d'un autre STATISTIC ou STORE
        if (normalizedStoreType === "STATISTIC" && (normalizedDependType === "STATISTIC" || normalizedDependType === "STORE")) {
            return false;
        }
        
        // Un magasin STORE ne peut pas dépendre d'un autre STORE
        if (normalizedStoreType === "STORE" && normalizedDependType === "STORE") {
            return false;
        }
        
        return true;
    }, [normalizeEntityType]);

    const handleFieldChange = (field: 'depends' | 'group', value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // Filtrer les magasins disponibles selon les règles de dépendance
    const filteredListStoresOptions = useMemo(() => {
        const currentStoreType = normalizeEntityType(store.ENTITY_TYPE);
        
        
        // IDs des magasins déjà dans le groupe
        const groupedEntityIds = new Set(formData.group.map((item: any) => 
            item.ENTITY_ID || item.value || item.entity_id
        ));
        
        const filtered = listStoresOptions
            .filter(item => {
                if (!item) return false;
                
                const itemId = item.data?.ENTITY_ID || item.value;
                if (!itemId) return false;
                
                // Ne pas inclure le magasin actuel
                if (itemId === store.ENTITY_ID) return false;
                
                // Ne pas inclure les magasins déjà groupés
                if (groupedEntityIds.has(itemId)) return false;
                
                // Récupérer le type d'entité
                let itemEntityType = null;
                if (item.data && item.data.ENTITY_TYPE) {
                    itemEntityType = item.data.ENTITY_TYPE;
                } else if (item.entity_type) {
                    itemEntityType = item.entity_type;
                }
                
                
                // Si pas de type, on autorise par défaut (comme dans BeOne Clean)
                if (!itemEntityType) {
                    return true;
                }
                
                // Appliquer les règles de dépendance
                const canDepend = canDependOn(currentStoreType, itemEntityType);
                return canDepend;
            })
            .map(item => ({
                ENTITY_ID: item.data?.ENTITY_ID || item.value,
                IDENTIFIER: item.data?.IDENTIFIER || (item.label && typeof item.label === 'string' ? item.label.split(' - ')[0] : ''),
                NAME: item.data?.NAME || (item.label && typeof item.label === 'string' ? item.label.split(' - ')[1] : ''),
                LOCAL_CITY_NAME: item.data?.LOCAL_CITY_NAME,
                ENTITY_TYPE: item.data?.ENTITY_TYPE || item.entity_type || 'N/A',
                label: item.label
            }));
            
        return filtered;
    }, [listStoresOptions, store.ENTITY_ID, store.ENTITY_TYPE, formData.group, canDependOn, normalizeEntityType]);

    // Texte du tooltip pour expliquer les règles
    const tooltipText = useMemo(() => {
        const storeType = normalizeEntityType(store.ENTITY_TYPE);
        
        if (storeType === "STATISTIC") {
            return t('pages.store.rules.statisticDepends');
        } else if (storeType === "STORE") {
            return t('pages.store.rules.storeDepends');
        }
        return "";
    }, [store.ENTITY_TYPE, t, normalizeEntityType]);
    
    const handleSave = async () => {
        const { depends, group } = formData;
        const payload: any = {};
        
        const LATITUDE = latitudeRef.current?.value || '';
        const LONGITUDE = longitudeRef.current?.value || '';
        const SUBSCRIBE_DATE = subscribeDateRef.current?.value || '';
        const UNSUBSCRIBE_DATE = unsubscribeDateRef.current?.value || '';
        const SIGNATURE = signatureRef.current?.value || '';
        const DESCRIPTION = descriptionRef.current?.value || '';
        const OPENING_HOURS = openingHoursRef.current?.value || '';
        
        // Champs standards - l'API attend des strings pour latitude/longitude
        if (LATITUDE) payload.latitude = LATITUDE.toString();
        if (LONGITUDE) payload.longitude = LONGITUDE.toString();
        if (SUBSCRIBE_DATE) payload.subscribe_date = SUBSCRIBE_DATE;
        if (UNSUBSCRIBE_DATE) payload.unsubscribe_date = UNSUBSCRIBE_DATE;
        if (SIGNATURE !== (store.SIGNATURE || '')) payload.signature = SIGNATURE;
        if (DESCRIPTION !== (store.DESCRIPTION || '')) payload.description = DESCRIPTION;
        if (OPENING_HOURS !== (store.OPENING_HOURS || '')) payload.opening_hours = OPENING_HOURS;
        
        // Gestion des dépendances (Depends On)
        const originalDepends = (store.PARENT || []).map((d: any) => d.ENTITY_ID).sort();
        const newDepends = depends.map((d: any) => d.ENTITY_ID).sort();
        
        if (JSON.stringify(originalDepends) !== JSON.stringify(newDepends)) {
            const toAdd = newDepends.filter((id: any) => !originalDepends.includes(id));
            const toRemove = originalDepends.filter((id: any) => !newDepends.includes(id));
            
            if (toAdd.length > 0) payload.add_depend_on = toAdd.join(',');
            if (toRemove.length > 0) payload.remove_depend_on = toRemove.join(',');
        }
        
        if (Object.keys(payload).length === 0) {
            onClose();
            return;
        }
        
        // OPTIMISTIC UPDATE: Mettre à jour immédiatement l'UI
        const optimisticUpdates: Partial<Store> = {};
        if (LATITUDE) {
            const formattedLat = LATITUDE.toString().replace(',', '.');
            optimisticUpdates.LATITUDE = parseFloat(formattedLat);
        }

        if (LONGITUDE) {
            const formattedLng = LONGITUDE.toString().replace(',', '.');
            optimisticUpdates.LONGITUDE = parseFloat(formattedLng);
        }
        if (SUBSCRIBE_DATE) optimisticUpdates.SUBSCRIBE_DATE = SUBSCRIBE_DATE;
        if (UNSUBSCRIBE_DATE) optimisticUpdates.UNSUBSCRIBE_DATE = UNSUBSCRIBE_DATE;
        if (SIGNATURE !== (store.SIGNATURE || '')) optimisticUpdates.SIGNATURE = SIGNATURE;
        if (DESCRIPTION !== (store.DESCRIPTION || '')) optimisticUpdates.DESCRIPTION = DESCRIPTION;
        if (OPENING_HOURS !== (store.OPENING_HOURS || '')) optimisticUpdates.OPENING_HOURS = OPENING_HOURS;
        if (JSON.stringify(originalDepends) !== JSON.stringify(newDepends)) {
            optimisticUpdates.PARENT = depends;
        }
        
        if (actualStore) {
            setActualStore({ ...actualStore, ...optimisticUpdates } as Store);
        }
        
        onClose();
        
        const result = await updateStore(store.ENTITY_ID.toString(), payload);
        
        if (result) {
            toast.success(t('pages.store.success.updated'));
            onSuccess?.();
        } else {
            if (actualStore) {
                setActualStore(actualStore);
            }
            toast.error(t('pages.store.errors.updateFailed'));
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                {t('pages.store.editAdditionalInfo')}
            </DialogTitle>
            <DialogContent sx={{ paddingTop: '20px!important' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Ligne 1: Latitude / Longitude */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                        <TextField
                            fullWidth
                            type="text"
                            inputMode="decimal"
                            label={t('pages.store.latitude')}
                            inputRef={latitudeRef}
                            defaultValue={store.LATITUDE?.toString() || ''}
                            inputProps={{ maxLength: 24 }}
                        />
                        <TextField
                            fullWidth
                            type="text"
                            inputMode="decimal"
                            label={t('pages.store.longitude')}
                            inputRef={longitudeRef}
                            defaultValue={store.LONGITUDE?.toString() || ''}
                            inputProps={{ maxLength: 24 }}
                        />
                    </Box>

                    {/* Ligne 2: Dates */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                        <TextField
                            fullWidth
                            type="date"
                            label={t('pages.store.subscribeDate')}
                            inputRef={subscribeDateRef}
                            defaultValue={formatDateForInput(store.SUBSCRIBE_DATE)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ onClick: (e: React.MouseEvent<HTMLInputElement>) => { (e.target as HTMLInputElement).showPicker?.(); } }}
                        />
                        <TextField
                            fullWidth
                            type="date"
                            label={t('pages.store.unsubscribeDate')}
                            inputRef={unsubscribeDateRef}
                            defaultValue={formatDateForInput(store.UNSUBSCRIBE_DATE)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ onClick: (e: React.MouseEvent<HTMLInputElement>) => { (e.target as HTMLInputElement).showPicker?.(); } }}
                        />
                    </Box>

                    {/* Ligne 3: Description / Signature */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label={t('pages.store.description')}
                            inputRef={descriptionRef}
                            defaultValue={store.DESCRIPTION || ''}
                            inputProps={{ maxLength: 4000 }}
                        />
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label={t('pages.store.signature')}
                            inputRef={signatureRef}
                            defaultValue={store.SIGNATURE || ''}
                            inputProps={{ maxLength: 4000 }}
                        />
                    </Box>

                    {/* Ligne 4: Heures d'ouverture / Dépend de */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                        <TextField
                            fullWidth
                            multiline
                            rows={7}
                            label={t('pages.store.openingHours')}
                            inputRef={openingHoursRef}
                            defaultValue={store.OPENING_HOURS || ''}
                            inputProps={{ maxLength: 4000 }}
                        />
                        
                        {!isCompany && (
                            <Box
                                sx={{
                                    height: 200,
                                    overflowX: 'hidden',
                                    border: '1px solid',
                                    borderColor: 'grey.400',
                                    borderRadius: '4px',
                                    p: 1.5,
                                }}
                            >
                                <Tooltip title={tooltipText} placement="top">
                                    <Box sx={{ height: '100%' }}>
                                        <StoreMultiAutocomplete
                                            stores={filteredListStoresOptions as Store[]}
                                            value={formData.depends as Store[]}
                                            onChange={(selectedStores) => handleFieldChange('depends', selectedStores)}
                                            label={t('pages.store.dependsOn')}
                                            placeholder={t('common.search')}
                                            loading={listStoresOptions === null || listStoresOptions === undefined}
                                        />
                                    </Box>
                                </Tooltip>
                                
                            </Box>
                        )}
                    </Box>

                    {/* Ligne 5: Regroupement de */}
                    <Box>
                        <Box
                            sx={{
                                height: '200px',
                                border: '1px solid',
                                borderColor: 'grey.400',
                                overflowX: 'hidden',
                                borderRadius: '4px',
                            }}
                        >
                            <List
                                dense
                                subheader={
                                    <ListSubheader disableSticky sx={{ lineHeight: '32px' }}>
                                        {t('pages.store.groupedWith')}
                                    </ListSubheader>
                                }
                                sx={{ p: 0 }}
                            >
                                {formData.group.length === 0 ? (
                                    <Typography 
                                        color="textSecondary" 
                                        sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'center', 
                                            alignItems: 'center', 
                                            height: 100,
                                            padding: '0 16px',
                                            textAlign: 'center'
                                        }}
                                    >
                                        {t('pages.store.noGroupedStores')}
                                    </Typography>
                                ) : (
                                    formData.group.map((item: any) => (
                                        <ListItem 
                                            key={item.ENTITY_ID} 
                                            sx={{ 
                                                padding: '2px 16px',
                                                minHeight: '36px'
                                            }}
                                            dense
                                        >
                                            <ListItemText 
                                                primary={getStoreSearchLabel(item)}
                                                primaryTypographyProps={{ fontSize: '0.9rem' }}
                                            />
                                        </ListItem>
                                    ))
                                )}
                            </List>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading} variant="outlined">
                    {t('common.cancel')}
                </Button>
                <Button 
                    variant="contained" 
                    onClick={handleSave} 
                    disabled={isLoading}
                    startIcon={isLoading ? <CircularProgress size={20} /> : null}
                >
                    {isLoading ? t('common.saving') : t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditAdditionalInfoDrawer;
